import { useMemo, useState } from "react";
import {
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
} from "@b2bportal/core-hotels";
import { LodgingMediaAssetEnum } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import type { HotelRoomCardDialogComponentProps } from "./HotelRoomCardDialog";

export const useHotelRoomCardDialog = ({
  room,
  nightCount,
  selectedProduct,
}: HotelRoomCardDialogComponentProps) => {
  const { t } = useI18nContext();

  const { formatFiatCurrency } = useI18nContext();

  const [selectedRate, setSelectedRate] = useState(room.products[0]);

  // Benefits are rate-level: show the rate the user selected on the card, falling
  // back to the first rate. Never surface a benefit a given rate does not include.
  const benefits = (selectedProduct ?? selectedRate)?.benefits ?? [];

  const nightlyPrice = formatFiatCurrency(selectedRate.perNightTotal.fiat);
  const totalPrice = formatFiatCurrency(selectedRate.tripTotal.fiat);

  const detailsImage = room.roomInfo.media.find((media) => {
    return (
      media.LodgingMediaAsset === LodgingMediaAssetEnum.NonResizableImage ||
      media.LodgingMediaAsset === LodgingMediaAssetEnum.ResizableImage
    );
  });

  const emitOpenDetailsEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelShopPageRoomsDetailsOpened
  );

  return useMemo(() => {
    return {
      context: {
        amenities: room.roomInfo.amenities,
        description: room.roomInfo.description,
        detailsImage: detailsImage,
        products: room.products,
        selectedRate,
        benefits,
      },
      handlers: {
        setSelectedRate,
        setSelectedRateWithRateId: (rateId: string) => {
          const product = room.products.find((p) => p.rateId?.value === rateId);
          if (product) setSelectedRate(product);
        },
        emitOpenDetailsEvent,
      },
      text: {
        roomDetailsTitle: t("core-hotels.roomSelection.roomDetailsTitle"),
        roomName: room.roomInfo.name
          ? (htmlDecode(room.roomInfo.name) ?? room.roomInfo.name)
          : undefined,
        guests: room.roomInfo.maxAdults
          ? t("core-hotels.roomSelection.roomFitsGuests", {
              count: room.roomInfo.maxAdults,
            })
          : undefined,
        beds: room.roomInfo.beds.description,
        viewDetails: t("core-hotels.roomSelection.viewDetails"),
        selectedPrice: t("core-hotels.shop.price.pricePerNight", {
          price: nightlyPrice,
        }),
        totalPrice: t("core-hotels.shop.price.priceForNightsWithTaxesAndFees", {
          price: totalPrice,
          count: nightCount,
        }),
        selectRoom: t("core-hotels.shop.price.roomCTA"),
        roomDescriptionTitle: t(
          "core-hotels.roomSelection.roomDescriptionTitle"
        ),
        roomDescription: room.roomInfo.description,
        amenitiesTitle: t("core-hotels.roomSelection.amenitiesTitle"),
        benefitsTitle: t("core-hotels.roomSelection.benefitsTitle"),
      },
    };
  }, [
    room,
    detailsImage,
    selectedRate,
    benefits,
    emitOpenDetailsEvent,
    t,
    nightlyPrice,
    totalPrice,
    nightCount,
  ]);
};
